import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import GlassCard from '../components/GlassCard';
import { familiesAPI, peopleAPI, contactsAPI, socialLinksAPI, userAPI } from '../services/api';
import { parseDateComponents, getMonthNameFull, getDay } from '../utils/dateUtils';

const HomePage = () => {
  const { theme } = useTheme();
  const { isAuthenticated, user } = useAuth();
  const [birthdayMembers, setBirthdayMembers] = useState([]);
  const [loadingBirthdays, setLoadingBirthdays] = useState(false);
  const [weekBirthdayMembers, setWeekBirthdayMembers] = useState([]);
  const [loadingWeekBirthdays, setLoadingWeekBirthdays] = useState(false);
  const [topContributors, setTopContributors] = useState([]);
  const [loadingContributors, setLoadingContributors] = useState(false);
  const [mostActiveUsers, setMostActiveUsers] = useState([]);
  const [loadingActiveUsers, setLoadingActiveUsers] = useState(false);
  const [activeUsersError, setActiveUsersError] = useState('');
  const [contributorsError, setContributorsError] = useState('');

  // Check if user is admin
  const isAdmin = user?.user?.is_admin === true || user?.is_admin === true;
  const [families, setFamilies] = useState([]);
  const [hasNoFamilyAccess, setHasNoFamilyAccess] = useState(false);
  const [isFamilyOwner, setIsFamilyOwner] = useState(false);

  // Load families to check access and ownership
  useEffect(() => {
    if (isAuthenticated) {
      loadFamiliesForAccessCheck();
    }
  }, [isAuthenticated, user]);

  const loadFamiliesForAccessCheck = async () => {
    try {
      const response = await familiesAPI.list();
      const familiesList = response.data.families || [];
      setFamilies(familiesList);
      
      // Check if user has no family access
      setHasNoFamilyAccess(familiesList.length === 0);
      
      // Check if user is owner/creator of any family
      // The user ID can be in different places depending on auth method
      // From /api/ft/me: { user: { id, ... }, auth: { user_id, ... } }
      const currentUserId = user?.user?.id || user?.auth?.user_id || user?.user_id || user?.sub;
      if (currentUserId && familiesList.length > 0) {
        const ownsFamily = familiesList.some(family => {
          // Compare as strings to handle UUID comparison
          return family.created_by && String(family.created_by) === String(currentUserId);
        });
        setIsFamilyOwner(ownsFamily);
      } else {
        setIsFamilyOwner(false);
      }
    } catch (err) {
      // If API call fails, assume no access (will show Manage Families)
      setHasNoFamilyAccess(true);
      setIsFamilyOwner(false);
    }
  };

  // Filter features based on admin status, family access, and ownership
  const allFeatures = [
    { icon: '👤', title: 'Members', description: 'Add and manage family members', link: '/people' },
    { icon: 'logo', title: 'Tree View', description: 'Visualize your family tree', link: '/tree' },
    { 
      icon: '👨‍👩‍👧‍👦', 
      title: 'Manage Families', 
      description: 'Create and organize family groups', 
      link: '/families',
      showFor: (isAdmin, hasNoAccess, isOwner) => isAdmin || hasNoAccess || isOwner
    },
  ];

  const features = allFeatures.filter(feature => {
    if (feature.showFor) {
      return feature.showFor(isAdmin, hasNoFamilyAccess, isFamilyOwner);
    }
    return true;
  });

  useEffect(() => {
    if (isAuthenticated) {
      loadBirthdayMembers();
      loadWeekBirthdayMembers();
      loadTopContributors();
      loadMostActiveUsers();
    }
  }, [isAuthenticated]);

  const loadBirthdayMembers = async () => {
    try {
      setLoadingBirthdays(true);
      const now = new Date();
      const currentMonth = now.getMonth() + 1; // 1-12
      const currentDay = now.getDate(); // 1-31

      // Get all families and load people
      const familiesRes = await familiesAPI.list();
      const families = familiesRes.data.families || [];
      
      const allPeople = [];
      for (const family of families) {
        try {
          const peopleRes = await peopleAPI.list(family.id);
          const people = peopleRes.data.people || [];
          allPeople.push(...people);
        } catch (e) {
          // Skip families we can't access
        }
      }

      // Filter people with today's birthday
      const todayBirthdays = allPeople.filter(person => {
        if (!person.date_of_birth) return false;
        const components = parseDateComponents(person.date_of_birth);
        if (!components) return false;
        return components.month === currentMonth && components.day === currentDay;
      });

      // Load full person data with relationships (to get contacts and social links)
      const membersWithDetails = await Promise.all(
        todayBirthdays.map(async (person) => {
          try {
            // Load basic person data
            const personResponse = await peopleAPI.get(person.id);
            const personData = personResponse.data || person;
            
            // Load contacts and social links separately
            let contacts = [];
            let socialLinks = [];
            
            try {
              const contactsRes = await contactsAPI.list(person.id);
              contacts = contactsRes.data?.contacts || [];
            } catch (e) {
              console.error(`Failed to load contacts for person ${person.id}:`, e);
            }
            
            try {
              const socialRes = await socialLinksAPI.list(person.id);
              socialLinks = socialRes.data?.social_links || [];
            } catch (e) {
              console.error(`Failed to load social links for person ${person.id}:`, e);
            }
            
            return {
              ...personData,
              contacts: contacts,
              social_links: socialLinks,
            };
          } catch (e) {
            console.error(`Failed to load details for person ${person.id}:`, e);
            // Return basic person with empty arrays if details fail
            return { ...person, contacts: [], social_links: [] };
          }
        })
      );

      setBirthdayMembers(membersWithDetails);
    } catch (err) {
      console.error('Failed to load birthday members:', err);
    } finally {
      setLoadingBirthdays(false);
    }
  };

  const loadWeekBirthdayMembers = async () => {
    try {
      setLoadingWeekBirthdays(true);
      const now = new Date();
      const currentMonth = now.getMonth() + 1; // 1-12
      const currentDay = now.getDate(); // 1-31
      
      // Calculate dates for the next 7 days (excluding today)
      const weekDates = [];
      for (let i = 1; i <= 7; i++) {
        const date = new Date(now);
        date.setDate(now.getDate() + i);
        weekDates.push({
          month: date.getMonth() + 1,
          day: date.getDate(),
        });
      }

      // Get all families and load people
      const familiesRes = await familiesAPI.list();
      const families = familiesRes.data.families || [];
      
      const allPeople = [];
      for (const family of families) {
        try {
          const peopleRes = await peopleAPI.list(family.id);
          const people = peopleRes.data.people || [];
          allPeople.push(...people);
        } catch (e) {
          // Skip families we can't access
        }
      }

      // Filter people with birthdays this week (excluding today)
      const weekBirthdays = allPeople.filter(person => {
        if (!person.date_of_birth) return false;
        const components = parseDateComponents(person.date_of_birth);
        if (!components) return false;
        
        // Check if birthday matches any of the week dates
        return weekDates.some(weekDate => 
          components.month === weekDate.month && components.day === weekDate.day
        );
      });

      // Load full person data with relationships (to get contacts and social links)
      const membersWithDetails = await Promise.all(
        weekBirthdays.map(async (person) => {
          try {
            // Load basic person data
            const personResponse = await peopleAPI.get(person.id);
            const personData = personResponse.data || person;
            
            // Load contacts and social links separately
            let contacts = [];
            let socialLinks = [];
            
            try {
              const contactsRes = await contactsAPI.list(person.id);
              contacts = contactsRes.data?.contacts || [];
            } catch (e) {
              console.error(`Failed to load contacts for person ${person.id}:`, e);
            }
            
            try {
              const socialRes = await socialLinksAPI.list(person.id);
              socialLinks = socialRes.data?.social_links || [];
            } catch (e) {
              console.error(`Failed to load social links for person ${person.id}:`, e);
            }
            
            // Get birthday month and day for display
            const components = parseDateComponents(personData.date_of_birth);
            const monthName = components ? getMonthNameFull(personData.date_of_birth) : '';
            const day = components ? getDay(personData.date_of_birth) : null;
            
            return {
              ...personData,
              contacts: contacts,
              social_links: socialLinks,
              birthdayMonth: monthName,
              birthdayDay: day,
            };
          } catch (e) {
            console.error(`Failed to load details for person ${person.id}:`, e);
            // Return basic person with empty arrays if details fail
            const components = parseDateComponents(person.date_of_birth);
            const monthName = components ? getMonthNameFull(person.date_of_birth) : '';
            const day = components ? getDay(person.date_of_birth) : null;
            return { 
              ...person, 
              contacts: [], 
              social_links: [],
              birthdayMonth: monthName,
              birthdayDay: day,
            };
          }
        })
      );

      // Sort by month and day
      membersWithDetails.sort((a, b) => {
        const aComponents = parseDateComponents(a.date_of_birth);
        const bComponents = parseDateComponents(b.date_of_birth);
        if (!aComponents || !bComponents) return 0;
        if (aComponents.month !== bComponents.month) {
          return aComponents.month - bComponents.month;
        }
        return aComponents.day - bComponents.day;
      });

      setWeekBirthdayMembers(membersWithDetails);
    } catch (err) {
      console.error('Failed to load week birthday members:', err);
    } finally {
      setLoadingWeekBirthdays(false);
    }
  };

  const loadTopContributors = async () => {
    try {
      setLoadingContributors(true);
      setContributorsError('');
      const response = await userAPI.topContributors();
      console.log('Top contributors response:', response.data);
      const contributors = response.data.contributors || [];
      console.log('Top contributors count:', contributors.length);
      // Limit to top 3 contributors
      setTopContributors(contributors.slice(0, 3));
    } catch (err) {
      console.error('Failed to load top contributors:', err);
      console.error('Error details:', err.response?.data);
      setContributorsError(err.response?.data?.error || err.message || 'Failed to load top contributors');
      setTopContributors([]);
    } finally {
      setLoadingContributors(false);
    }
  };

  const loadMostActiveUsers = async () => {
    try {
      setLoadingActiveUsers(true);
      setActiveUsersError('');
      const response = await userAPI.mostActiveUsers();
      console.log('Most active users response:', response.data);
      const users = response.data.users || [];
      console.log('Most active users count:', users.length);
      // Limit to top 3 visitors
      setMostActiveUsers(users.slice(0, 3));
    } catch (err) {
      console.error('Failed to load most active users:', err);
      console.error('Error details:', err.response?.data);
      setActiveUsersError(err.response?.data?.error || err.message || 'Failed to load most active users');
      setMostActiveUsers([]);
    } finally {
      setLoadingActiveUsers(false);
    }
  };

  const getWhatsAppNumber = (person) => {
    // Check contacts for WhatsApp or phone
    if (person.contacts && person.contacts.length > 0) {
      // Prefer WhatsApp contact type, fallback to phone
      const whatsappContact = person.contacts.find(c => c.contact_type === 'whatsapp') ||
                              person.contacts.find(c => c.contact_type === 'phone');
      if (whatsappContact && whatsappContact.value) {
        // Clean phone number (remove spaces, dashes, parentheses, etc.)
        let phone = whatsappContact.value.replace(/[\s\-\(\)\.]/g, '');
        // If it doesn't start with +, try to format it
        if (!phone.startsWith('+')) {
          // If it's just digits, assume it might need country code, but for now use as is
          // WhatsApp will handle it based on the user's location
          phone = phone;
        }
        return phone;
      }
    }
    return null;
  };

  const getSocialLinks = (person) => {
    if (person.social_links && person.social_links.length > 0) {
      return person.social_links.filter(link => link.platform !== 'whatsapp');
    }
    return [];
  };

  const formatWhatsAppLink = (phone) => {
    // WhatsApp API link format: https://wa.me/PHONENUMBER (without + sign)
    // Remove all non-digit characters except +
    let cleaned = phone.replace(/[^\d+]/g, '');
    // Remove + from the start if present (wa.me expects just numbers)
    cleaned = cleaned.replace(/^\+/, '');
    return `https://wa.me/${cleaned}`;
  };

  const getSocialLinkIcon = (platform) => {
    const platformLower = platform?.toLowerCase();
    const icons = {
      facebook: '📘',
      instagram: '📷',
      twitter: '🐦',
      x: '🐦', // Twitter/X
      linkedin: '💼',
      youtube: '📺',
      whatsapp: '💬',
      github: '🐙',
      website: '🌐',
      tiktok: '🎵',
      other: '🔗',
    };
    return icons[platformLower] || '🔗';
  };

  const getSocialLinkColor = (platform) => {
    const platformLower = platform?.toLowerCase();
    const colors = {
      facebook: '#1877F2',
      instagram: '#E4405F',
      twitter: '#1DA1F2',
      x: '#000000', // Twitter/X black
      linkedin: '#0077B5',
      youtube: '#FF0000',
      github: '#181717',
      website: theme.accent.primary || '#3B82F6',
      tiktok: '#000000',
      other: theme.accent.primary || '#3B82F6',
    };
    return colors[platformLower] || theme.accent.primary || '#3B82F6';
  };

  return (
    <div style={{ padding: '16px 0', width: '100%' }} className="home-page">
      {/* Today's Birthdays Section */}
      {isAuthenticated && (
        <GlassCard style={{ marginBottom: '24px', padding: 'clamp(16px, 4vw, 24px)' }}>
          <h2 style={{ 
            fontSize: 'clamp(20px, 4vw, 24px)', 
            marginBottom: '16px', 
            color: theme.text.primary,
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}>
            🎉 Today's Birthdays
          </h2>
          {loadingBirthdays ? (
            <p style={{ color: theme.text.secondary, fontSize: 'clamp(14px, 2.5vw, 16px)' }}>
              Loading birthdays...
            </p>
          ) : birthdayMembers.length > 0 ? (
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 300px), 1fr))', 
              gap: '16px' 
            }}
            className="birthday-members-grid"
            >
              {birthdayMembers.map((person) => {
                const whatsappNumber = getWhatsAppNumber(person);
                const socialLinks = getSocialLinks(person);
                const hasGreetingOptions = whatsappNumber || socialLinks.length > 0;

                return (
                  <div
                    key={person.id}
                    style={{
                      padding: 'clamp(12px, 2.5vw, 16px)',
                      borderRadius: '8px',
                      background: theme.card.background,
                      border: theme.glass.border,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '12px',
                    }}
                  >
                    <Link
                      to={`/people/${person.id}`}
                      style={{
                        textDecoration: 'none',
                        color: 'inherit',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                      }}
                    >
                      {person.picture_url ? (
                        <img
                          src={person.picture_url}
                          alt={person.preferred_name || person.first_name}
                          style={{
                            width: 'clamp(50px, 10vw, 60px)',
                            height: 'clamp(50px, 10vw, 60px)',
                            borderRadius: '50%',
                            objectFit: 'cover',
                            border: theme.glass.border,
                            flexShrink: 0,
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            width: 'clamp(50px, 10vw, 60px)',
                            height: 'clamp(50px, 10vw, 60px)',
                            borderRadius: '50%',
                            background: theme.card.background,
                            border: theme.glass.border,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 'clamp(24px, 5vw, 30px)',
                            flexShrink: 0,
                          }}
                        >
                          👤
                        </div>
                      )}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ 
                          fontSize: 'clamp(16px, 3vw, 18px)', 
                          fontWeight: 600, 
                          color: theme.text.primary,
                          wordBreak: 'break-word',
                          marginBottom: '4px',
                        }}>
                          {person.preferred_name || person.first_name} {person.last_name || ''}
                        </div>
                        {person.date_of_birth && (
                          <div style={{ 
                            fontSize: 'clamp(12px, 2.5vw, 14px)', 
                            color: theme.text.secondary 
                          }}>
                            🎂 Birthday today!
                          </div>
                        )}
                      </div>
                    </Link>

                    {/* Greeting Options */}
                    {hasGreetingOptions && (
                      <div style={{
                        paddingTop: '12px',
                        borderTop: theme.glass.border,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px',
                      }}>
                        <div style={{
                          fontSize: 'clamp(12px, 2.5vw, 13px)',
                          color: theme.text.secondary,
                          marginBottom: '4px',
                        }}>
                          Send Greetings:
                        </div>
                        <div style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: '8px',
                        }}>
                          {whatsappNumber && (
                            <a
                              href={formatWhatsAppLink(whatsappNumber)}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                padding: '8px 12px',
                                borderRadius: '6px',
                                background: '#25D366',
                                color: 'white',
                                textDecoration: 'none',
                                fontSize: 'clamp(12px, 2.5vw, 14px)',
                                fontWeight: 500,
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                minHeight: '36px',
                                transition: 'all 0.2s ease',
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.opacity = '0.9';
                                e.currentTarget.style.transform = 'translateY(-1px)';
                                e.currentTarget.style.boxShadow = '0 2px 8px rgba(37, 211, 102, 0.3)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.opacity = '1';
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = 'none';
                              }}
                            >
                              <span style={{ fontSize: '16px' }}>💬</span>
                              <span>WhatsApp</span>
                            </a>
                          )}
                          {socialLinks.map((link) => {
                            const linkColor = getSocialLinkColor(link.platform);
                            return (
                              <a
                                key={link.id}
                                href={link.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  padding: '8px 12px',
                                  borderRadius: '6px',
                                  background: linkColor + '15',
                                  color: linkColor,
                                  textDecoration: 'none',
                                  fontSize: 'clamp(12px, 2.5vw, 14px)',
                                  fontWeight: 500,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px',
                                  minHeight: '36px',
                                  border: `1px solid ${linkColor}40`,
                                  transition: 'all 0.2s ease',
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.background = linkColor + '25';
                                  e.currentTarget.style.transform = 'translateY(-1px)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.background = linkColor + '15';
                                  e.currentTarget.style.transform = 'translateY(0)';
                                }}
                              >
                                <span style={{ fontSize: '16px' }}>{getSocialLinkIcon(link.platform)}</span>
                                <span>
                                  {link.platform === 'x' ? 'X (Twitter)' : 
                                   link.platform.charAt(0).toUpperCase() + link.platform.slice(1)}
                                </span>
                              </a>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <p style={{ 
              color: theme.text.secondary, 
              fontSize: 'clamp(14px, 2.5vw, 16px)',
              fontStyle: 'italic',
            }}>
              No birthdays today. Check back tomorrow! 🎉
            </p>
          )}
        </GlassCard>
      )}

      {/* Birthdays This Week Section */}
      {isAuthenticated && (
        <GlassCard style={{ marginBottom: '24px', padding: 'clamp(16px, 4vw, 24px)' }}>
          <h2 style={{ 
            fontSize: 'clamp(20px, 4vw, 24px)', 
            marginBottom: '16px', 
            color: theme.text.primary,
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}>
            📅 Birthdays This Week
          </h2>
          {loadingWeekBirthdays ? (
            <p style={{ color: theme.text.secondary, fontSize: 'clamp(14px, 2.5vw, 16px)' }}>
              Loading birthdays...
            </p>
          ) : weekBirthdayMembers.length > 0 ? (
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 300px), 1fr))', 
              gap: '16px' 
            }}
            className="week-birthday-members-grid"
            >
              {weekBirthdayMembers.map((person) => {
                const whatsappNumber = getWhatsAppNumber(person);
                const socialLinks = getSocialLinks(person);
                const hasGreetingOptions = whatsappNumber || socialLinks.length > 0;

                return (
                  <div
                    key={person.id}
                    style={{
                      padding: 'clamp(12px, 2.5vw, 16px)',
                      borderRadius: '8px',
                      background: theme.card.background,
                      border: theme.glass.border,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '12px',
                    }}
                  >
                    <Link
                      to={`/people/${person.id}`}
                      style={{
                        textDecoration: 'none',
                        color: 'inherit',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                      }}
                    >
                      {person.picture_url ? (
                        <img
                          src={person.picture_url}
                          alt={person.preferred_name || person.first_name}
                          style={{
                            width: 'clamp(50px, 10vw, 60px)',
                            height: 'clamp(50px, 10vw, 60px)',
                            borderRadius: '50%',
                            objectFit: 'cover',
                            border: theme.glass.border,
                            flexShrink: 0,
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            width: 'clamp(50px, 10vw, 60px)',
                            height: 'clamp(50px, 10vw, 60px)',
                            borderRadius: '50%',
                            background: theme.card.background,
                            border: theme.glass.border,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 'clamp(24px, 5vw, 30px)',
                            flexShrink: 0,
                          }}
                        >
                          👤
                        </div>
                      )}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ 
                          fontSize: 'clamp(16px, 3vw, 18px)', 
                          fontWeight: 600, 
                          color: theme.text.primary,
                          wordBreak: 'break-word',
                          marginBottom: '4px',
                        }}>
                          {person.preferred_name || person.first_name} {person.last_name || ''}
                        </div>
                        {person.birthdayMonth && person.birthdayDay && (
                          <div style={{ 
                            fontSize: 'clamp(12px, 2.5vw, 14px)', 
                            color: theme.text.secondary 
                          }}>
                            🎂 {person.birthdayMonth} {person.birthdayDay}
                          </div>
                        )}
                      </div>
                    </Link>

                    {/* Greeting Options */}
                    {hasGreetingOptions && (
                      <div style={{
                        paddingTop: '12px',
                        borderTop: theme.glass.border,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px',
                      }}>
                        <div style={{
                          fontSize: 'clamp(12px, 2.5vw, 13px)',
                          color: theme.text.secondary,
                          marginBottom: '4px',
                        }}>
                          Send Greetings:
                        </div>
                        <div style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: '8px',
                        }}>
                          {whatsappNumber && (
                            <a
                              href={formatWhatsAppLink(whatsappNumber)}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                padding: '8px 12px',
                                borderRadius: '6px',
                                background: '#25D366',
                                color: 'white',
                                textDecoration: 'none',
                                fontSize: 'clamp(12px, 2.5vw, 14px)',
                                fontWeight: 500,
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                minHeight: '36px',
                                transition: 'all 0.2s ease',
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.opacity = '0.9';
                                e.currentTarget.style.transform = 'translateY(-1px)';
                                e.currentTarget.style.boxShadow = '0 2px 8px rgba(37, 211, 102, 0.3)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.opacity = '1';
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = 'none';
                              }}
                            >
                              <span style={{ fontSize: '16px' }}>💬</span>
                              <span>WhatsApp</span>
                            </a>
                          )}
                          {socialLinks.map((link) => {
                            const linkColor = getSocialLinkColor(link.platform);
                            return (
                              <a
                                key={link.id}
                                href={link.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  padding: '8px 12px',
                                  borderRadius: '6px',
                                  background: linkColor + '15',
                                  color: linkColor,
                                  textDecoration: 'none',
                                  fontSize: 'clamp(12px, 2.5vw, 14px)',
                                  fontWeight: 500,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px',
                                  minHeight: '36px',
                                  border: `1px solid ${linkColor}40`,
                                  transition: 'all 0.2s ease',
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.background = linkColor + '25';
                                  e.currentTarget.style.transform = 'translateY(-1px)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.background = linkColor + '15';
                                  e.currentTarget.style.transform = 'translateY(0)';
                                }}
                              >
                                <span style={{ fontSize: '16px' }}>{getSocialLinkIcon(link.platform)}</span>
                                <span>
                                  {link.platform === 'x' ? 'X (Twitter)' : 
                                   link.platform.charAt(0).toUpperCase() + link.platform.slice(1)}
                                </span>
                              </a>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <p style={{ 
              color: theme.text.secondary, 
              fontSize: 'clamp(14px, 2.5vw, 16px)',
              fontStyle: 'italic',
            }}>
              No birthdays this week. 🎉
            </p>
          )}
        </GlassCard>
      )}

      {/* Top Contributors Leaderboard */}
      {isAuthenticated && (
        <GlassCard style={{ marginBottom: '24px', padding: 'clamp(16px, 4vw, 24px)' }}>
          <h2 style={{ 
            fontSize: 'clamp(20px, 4vw, 24px)', 
            marginBottom: '16px', 
            color: theme.text.primary,
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}>
            🏆 Top Contributors
          </h2>
          <p style={{ 
            fontSize: 'clamp(13px, 2.5vw, 14px)', 
            color: theme.text.secondary,
            marginBottom: '16px',
          }}>
            Members who have added the most family members
          </p>
          {loadingContributors ? (
            <p style={{ color: theme.text.secondary, fontSize: 'clamp(14px, 2.5vw, 16px)' }}>
              Loading contributors...
            </p>
          ) : contributorsError ? (
            <p style={{ 
              color: '#ff4444', 
              fontSize: 'clamp(14px, 2.5vw, 16px)',
            }}>
              ⚠️ {contributorsError}
            </p>
          ) : topContributors.length > 0 ? (
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 200px), 1fr))', 
              gap: '16px' 
            }}
            className="top-contributors-grid"
            >
              {topContributors.map((contributor, index) => {
                const personLink = contributor.claimed_person_id ? `/people/${contributor.claimed_person_id}` : null;
                const CardWrapper = personLink ? Link : 'div';
                const wrapperProps = personLink ? { to: personLink, style: { textDecoration: 'none', color: 'inherit', width: '100%' } } : {};
                
                return (
                <CardWrapper key={contributor.id} {...wrapperProps}>
                <div
                  style={{
                    padding: 'clamp(12px, 2.5vw, 16px)',
                    borderRadius: '8px',
                    background: theme.card.background,
                    border: theme.glass.border,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '12px',
                    textAlign: 'center',
                    position: 'relative',
                    cursor: personLink ? 'pointer' : 'default',
                    transition: personLink ? 'transform 0.2s ease, box-shadow 0.2s ease' : 'none',
                  }}
                  onMouseEnter={personLink ? (e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                  } : undefined}
                  onMouseLeave={personLink ? (e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  } : undefined}
                >
                  {/* Rank Badge */}
                  {index < 3 && (
                    <div style={{
                      position: 'absolute',
                      top: '8px',
                      right: '8px',
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      background: index === 0 ? '#FFD700' : index === 1 ? '#C0C0C0' : '#CD7F32',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      color: 'white',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                    }}>
                      {index === 0 ? '🥇' : index === 1 ? '🥈' : '🥉'}
                    </div>
                  )}
                  
                  {/* Avatar - Use claimed person's picture if available, otherwise user avatar */}
                  {contributor.picture_url || contributor.avatar_url ? (
                    <img
                      src={contributor.picture_url || contributor.avatar_url}
                      alt={contributor.full_name || contributor.email}
                      style={{
                        width: 'clamp(60px, 12vw, 80px)',
                        height: 'clamp(60px, 12vw, 80px)',
                        borderRadius: '50%',
                        objectFit: 'cover',
                        border: theme.glass.border,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        width: 'clamp(60px, 12vw, 80px)',
                        height: 'clamp(60px, 12vw, 80px)',
                        borderRadius: '50%',
                        background: theme.card.background,
                        border: theme.glass.border,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 'clamp(30px, 6vw, 40px)',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      }}
                    >
                      👤
                    </div>
                  )}
                  
                  {/* Name */}
                  <div style={{ flex: 1, minWidth: 0, width: '100%' }}>
                    <div style={{ 
                      fontSize: 'clamp(14px, 2.5vw, 16px)', 
                      fontWeight: 600, 
                      color: theme.text.primary,
                      wordBreak: 'break-word',
                      marginBottom: '4px',
                    }}>
                      {contributor.full_name || contributor.email}
                    </div>
                    
                    {/* Member Count */}
                    <div style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '6px 12px',
                      borderRadius: '20px',
                      background: theme.accent.primary + '20',
                      color: theme.accent.primary,
                      fontSize: 'clamp(13px, 2.5vw, 15px)',
                      fontWeight: 600,
                      marginTop: '8px',
                    }}>
                      <span>👥</span>
                      <span>{contributor.member_count} {contributor.member_count === 1 ? 'member' : 'members'}</span>
                    </div>
                  </div>
                </div>
                </CardWrapper>
              );
              })}
            </div>
          ) : (
            <p style={{ 
              color: theme.text.secondary, 
              fontSize: 'clamp(14px, 2.5vw, 16px)',
              fontStyle: 'italic',
            }}>
              No contributors yet. Start adding members to see the leaderboard! 🎯
            </p>
          )}
        </GlassCard>
      )}

      {/* Most Active Users Leaderboard */}
      {isAuthenticated && (
        <GlassCard style={{ padding: 'clamp(20px, 4vw, 24px)', marginBottom: '24px' }}>
          <h2 style={{ 
            fontSize: 'clamp(22px, 4.5vw, 28px)', 
            margin: '0 0 12px 0', 
            color: theme.text.primary,
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}>
            🏃 Most Active Members
          </h2>
          <p style={{ 
            color: theme.text.secondary, 
            fontSize: 'clamp(13px, 2.5vw, 15px)', 
            marginBottom: '20px',
            marginTop: '8px',
          }}>
            Members with the most views this month
          </p>
          {loadingActiveUsers ? (
            <p style={{ color: theme.text.secondary, fontSize: 'clamp(14px, 2.5vw, 16px)' }}>
              Loading active users...
            </p>
          ) : activeUsersError ? (
            <p style={{ 
              color: '#ff4444', 
              fontSize: 'clamp(14px, 2.5vw, 16px)',
            }}>
              ⚠️ {activeUsersError}
            </p>
          ) : mostActiveUsers.length > 0 ? (
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 200px), 1fr))', 
              gap: '16px' 
            }}
            className="most-active-users-grid"
            >
              {mostActiveUsers.map((user, index) => {
                const personLink = user.claimed_person_id ? `/people/${user.claimed_person_id}` : null;
                const CardWrapper = personLink ? Link : 'div';
                const wrapperProps = personLink ? { to: personLink, style: { textDecoration: 'none', color: 'inherit', width: '100%' } } : {};
                
                return (
                <CardWrapper key={user.id} {...wrapperProps}>
                <div
                  style={{
                    padding: 'clamp(12px, 2.5vw, 16px)',
                    borderRadius: '8px',
                    background: theme.card.background,
                    border: theme.glass.border,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '12px',
                    textAlign: 'center',
                    position: 'relative',
                    cursor: personLink ? 'pointer' : 'default',
                    transition: personLink ? 'transform 0.2s ease, box-shadow 0.2s ease' : 'none',
                  }}
                  onMouseEnter={personLink ? (e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                  } : undefined}
                  onMouseLeave={personLink ? (e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  } : undefined}
                >
                  {/* Rank Badge */}
                  {index < 3 && (
                    <div style={{
                      position: 'absolute',
                      top: '8px',
                      right: '8px',
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      background: index === 0 ? '#FFD700' : index === 1 ? '#C0C0C0' : '#CD7F32',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      color: 'white',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                    }}>
                      {index === 0 ? '🥇' : index === 1 ? '🥈' : '🥉'}
                    </div>
                  )}
                  
                  {/* Avatar - Use claimed person's picture if available, otherwise user avatar */}
                  {user.picture_url || user.avatar_url ? (
                    <img
                      src={user.picture_url || user.avatar_url}
                      alt={user.full_name || user.email}
                      style={{
                        width: 'clamp(60px, 12vw, 80px)',
                        height: 'clamp(60px, 12vw, 80px)',
                        borderRadius: '50%',
                        objectFit: 'cover',
                        border: theme.glass.border,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        width: 'clamp(60px, 12vw, 80px)',
                        height: 'clamp(60px, 12vw, 80px)',
                        borderRadius: '50%',
                        background: theme.card.background,
                        border: theme.glass.border,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 'clamp(30px, 6vw, 40px)',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      }}
                    >
                      👤
                    </div>
                  )}
                  
                  {/* Name */}
                  <div style={{ flex: 1, minWidth: 0, width: '100%' }}>
                    <div style={{ 
                      fontSize: 'clamp(14px, 2.5vw, 16px)', 
                      fontWeight: 600, 
                      color: theme.text.primary,
                      wordBreak: 'break-word',
                      marginBottom: '4px',
                    }}>
                      {user.full_name || user.email}
                    </div>
                    
                    {/* View Count */}
                    <div style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '6px 12px',
                      borderRadius: '20px',
                      background: theme.accent.primary + '20',
                      color: theme.accent.primary,
                      fontSize: 'clamp(13px, 2.5vw, 15px)',
                      fontWeight: 600,
                      marginTop: '8px',
                    }}>
                      <span>👁️</span>
                      <span>{user.login_count} {user.login_count === 1 ? 'view' : 'views'}</span>
                    </div>
                  </div>
                </div>
                </CardWrapper>
              );
              })}
            </div>
          ) : (
            <p style={{ 
              color: theme.text.secondary, 
              fontSize: 'clamp(14px, 2.5vw, 16px)',
              fontStyle: 'italic',
            }}>
              No active users yet. Start viewing pages to see the leaderboard! 🎯
            </p>
          )}
        </GlassCard>
      )}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))',
          gap: '16px',
          marginTop: '24px',
        }}
      >
        {features.map((feature) => (
          <Link
            key={feature.link}
            to={feature.link}
            style={{ textDecoration: 'none', color: 'inherit', minWidth: 0 }}
          >
            <GlassCard
              style={{
                textAlign: 'center',
                padding: '24px 16px',
                height: '100%',
              }}
            >
              <div style={{ fontSize: 'clamp(36px, 6vw, 48px)', marginBottom: '12px', display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '48px' }}>
                {feature.icon === 'logo' ? (
                  <img
                    src="/assets/family-tree-logo.png"
                    alt="Family Tree"
                    style={{
                      height: 'clamp(36px, 6vw, 48px)',
                      width: 'auto',
                      objectFit: 'contain',
                    }}
                  />
                ) : (
                  feature.icon
                )}
              </div>
              <h2 style={{ fontSize: 'clamp(18px, 3vw, 24px)', marginBottom: '8px', color: theme.text.primary }}>
                {feature.title}
              </h2>
              <p style={{ color: theme.text.secondary, fontSize: 'clamp(14px, 2vw, 16px)' }}>{feature.description}</p>
            </GlassCard>
          </Link>
        ))}
      </div>
    </div>
  );
};

export default HomePage;

